import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin, ArrowUp } from 'lucide-react'
import OptimizedImage from './OptimizedImage'
import { BsWhatsapp } from 'react-icons/bs'

const Footer = () => {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const quickLinks = [
    { href: '#home', label: t('nav.home') },
    { href: '#about', label: t('nav.about') },
    { href: '#academics', label: t('nav.academics') },
    { href: '#gallery', label: t('nav.gallery') },
    { href: '#admissions', label: t('nav.admissions') },
    { href: '#contact', label: t('nav.contact') }
  ]

  const socialLinks = [
    {
      icon: <Facebook className="w-5 h-5" />,
      href: 'https://www.facebook.com/share/1D8rWxHC1X/',
      label: t('social.facebook'),
      color: 'hover:text-blue-600'
    },
    {
      icon: <Twitter className="w-5 h-5" />,
      href: 'https://x.com/MandavadSankul',
      label: t('social.twitter'),
      color: 'hover:text-blue-400'
    },
    {
      icon: <Instagram className="w-5 h-5" />,
      href: 'https://www.instagram.com/mandavadsankul1996?igsh=a3owY2NqNHE3aXB0',
      label: t('social.instagram'),
      color: 'hover:text-pink-600'
    },
    {
      icon: <Youtube className="w-5 h-5" />,
      href: 'https://youtube.com/@mandavadsankul?si=T0SA_iABuIu7mjGh',
      label: t('social.youtube'),
      color: 'hover:text-red-600'
    },
    {
      icon: <BsWhatsapp className="w-5 h-5" />,
      href: 'https://wa.me/919687685102',
      label: 'WhatsApp',
      color: 'hover:text-green-600'
    }
  ]

  return (
    <footer className="bg-gradient-to-br from-primary-purple to-primary-blue text-white relative overflow-hidden">
      {/* Background Patterns */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full translate-x-24 translate-y-24"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white rounded-full"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* School Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <OptimizedImage
                  src="/logo.jpeg" 
                  alt="Shri V.D. Patel Madhyamik School Logo" 
                  className="w-12 h-12 rounded-full object-cover"
                  loading="lazy"
                  width={48}
                  height={48}
                />
                <div>
                  <h3 className="text-2xl font-bold font-poppins">{t('footer.schoolName')}</h3>
                  <p className="text-sm opacity-80">{t('footer.trustName')}</p>
                </div>
              </div>
              
              <p className="text-gray-200 leading-relaxed mb-6 max-w-md">
                {t('footer.description')}
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-primary-yellow" />
                  <span className="text-sm">
                    {t('footer.address')}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-primary-yellow" />
                  <span className="text-sm">
                    +91 9687685102 / +91 9687685108
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-primary-yellow" />
                  <span className="text-sm">
                    mandavadsankul1996@gmail.com
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6 font-montserrat">{t('footer.quickLinks')}</h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <a 
                      href={link.href}
                      className="text-gray-200 hover:text-primary-yellow transition-colors duration-300 text-sm"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Additional Links & Social */}
            <div>
              <h4 className="text-lg font-semibold mb-6 font-montserrat">{t('footer.followUs')}</h4>
              
              {/* Additional Links */}
              <ul className="space-y-3 mb-8">
                <li>
                  <a href="#" className="text-gray-200 hover:text-primary-yellow transition-colors duration-300 text-sm">
                    {t('footer.privacyPolicy')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-200 hover:text-primary-yellow transition-colors duration-300 text-sm">
                    {t('footer.termsOfUse')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-200 hover:text-primary-yellow transition-colors duration-300 text-sm">
                    {t('footer.careerOpportunities')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-200 hover:text-primary-yellow transition-colors duration-300 text-sm">
                    {t('footer.alumniNetwork')}
                  </a>
                </li>
              </ul>

              {/* Social Media */}
              <div>
                <h5 className="font-semibold mb-4 text-primary-yellow">{t('footer.followUs')}</h5>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.href}
                      aria-label={social.label}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 bg-white/10 rounded-full flex items-center justify-center transition-all duration-300 hover:bg-white/20 hover:scale-110 ${social.color}`}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-white/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div>
                <h4 className="text-lg font-semibold mb-2">{t('footer.newsletter.title')}</h4>
                <p className="text-gray-200 text-sm">{t('footer.newsletter.subtitle')}</p>
              </div>
              <div className="flex w-full md:w-auto">
                <form
                  className="flex w-full"
                  aria-label={t('footer.newsletter.title')}
                  onSubmit={(event) => event.preventDefault()}
                >
                  <label htmlFor="newsletter-email" className="sr-only">
                    {t('footer.newsletter.emailPlaceholder')}
                  </label>
                  <input
                    id="newsletter-email"
                    type="email"
                    placeholder={t('footer.newsletter.emailPlaceholder')}
                    className="flex-1 md:w-64 px-4 py-3 rounded-l-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-yellow"
                    autoComplete="email"
                  />
                  <button
                    type="submit"
                    className="bg-primary-yellow text-primary-purple px-6 py-3 rounded-r-lg font-semibold hover:bg-yellow-400 transition-colors duration-300"
                  >
                    {t('footer.newsletter.subscribeButton')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-white/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="text-center md:text-left">
                <p className="text-gray-200 text-sm">
                  {t('footer.copyright', { year: currentYear })}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  {t('footer.developedBy')}
                </p>
              </div>
              
              {/* Back to Top Button */}
              <button
                onClick={scrollToTop}
                className="w-12 h-12 bg-primary-yellow text-primary-purple rounded-full flex items-center justify-center hover:bg-yellow-400 hover:scale-110 transition-all duration-300 shadow-lg"
                aria-label={t('footer.backToTop')}
              >
                <ArrowUp className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer